import React from 'react'

import { Edit, MoreVertical, Trash2 } from 'lucide-react'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Badge } from '@/shared/components/atoms/Badge/Badge'
import { Button } from '@/shared/components/atoms/Button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/shared/components/atoms/Dropdown/Dropdown'

import { User } from './Users.types'
import {
	getUserRoleName,
	getRoleDisplayName,
	formatUserName,
	isSuperAdminRoleName,
} from './Users.utils'

interface UserRowProps {
	user: User
	currentUserAccountId?: number
	canEdit?: boolean
	canDelete?: boolean
	onEdit: (user: User) => void
	onRemove: (user: User) => void
}

/**
 * Individual user row component
 */
export const UserRow: React.FC<UserRowProps> = ({
	user,
	currentUserAccountId,
	canEdit = false,
	canDelete = false,
	onEdit,
	onRemove,
}) => {
	const { t } = useTranslation('common')
	const roleName = getUserRoleName(user)
	const userName = formatUserName(user)
	const isSuperAdmin = isSuperAdminRoleName(roleName)
	const displayRoleName = getRoleDisplayName(roleName)

	// Get account ID from user.account?.id (from users endpoint structure)
	const userAccountId = user.account?.id

	// Check if this user's account ID matches the current user's account ID from /me endpoint
	const isCurrentUser =
		currentUserAccountId !== undefined &&
		userAccountId !== undefined &&
		currentUserAccountId === userAccountId

	const hasDropdownItems = canEdit || (!isCurrentUser && canDelete)

	const renderDropdownMenu = () => {
		if (!hasDropdownItems) return null

		return (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="ghost" size="icon" className="h-8 w-8">
						<MoreVertical className="h-4 w-4" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					{canEdit && (
						<DropdownMenuItem onClick={() => onEdit(user)}>
							<Edit className="mr-2 h-4 w-4" />
							{t('account.users.menu.edit')}
						</DropdownMenuItem>
					)}
					{!isCurrentUser && canDelete && (
						<DropdownMenuItem
							onClick={() => onRemove(user)}
							variant="destructive"
						>
							<Trash2 className="mr-2 h-4 w-4" />
							{t('account.users.menu.remove')}
						</DropdownMenuItem>
					)}
				</DropdownMenuContent>
			</DropdownMenu>
		)
	}

	return (
		<div className="flex flex-row md:block justify-between p-6 hover:bg-muted/50 transition-colors">
			<div className="min-w-0 flex-1 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
				{/* Mobile layout */}
				<div className="flex items-center justify-between lg:hidden">
					<h3 className="font-semibold text-foreground">{userName}</h3>
				</div>

				{/* Desktop layout - username only */}
				<div className="hidden lg:block">
					<h3 className="font-semibold text-foreground">{userName}</h3>
				</div>

				{/* User details */}
				<div className="min-w-0 flex flex-col lg:flex-row lg:items-center md:gap-2 text-sm text-muted-foreground">
					<span className="block min-w-0 max-w-full truncate text-xs lg:text-sm">
						{user.email}
					</span>

					<span className="hidden lg:block">•</span>
					<span>{user.accountName}</span>
				</div>

				{/* Desktop badge and dropdown */}
				<div className="hidden lg:flex items-center gap-3">
					<div className="relative group">
						<Badge variant="outline">{displayRoleName}</Badge>
						{isSuperAdmin && (
							<div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
								{displayRoleName}
							</div>
						)}
					</div>
					{renderDropdownMenu()}
				</div>
			</div>
			{/* Mobile badge - right col */}
			<div className="flex items-center justify-end">
				<div className="lg:hidden relative group">
					<Badge
						variant="outline"
						className="px-2 py-0.5 text-[12px] bg-muted/50"
					>
						{displayRoleName}
					</Badge>
					{isSuperAdmin && (
						<div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
							{displayRoleName}
						</div>
					)}
				</div>

				<div className="md:hidden">{renderDropdownMenu()}</div>
			</div>
		</div>
	)
}
